/**
 * useRoleCode — decode the caller's role from the Cognito access token.
 *
 * Phase 9 / Plan 09-04 — the server resolves roleCode via DB lookup (Redis cache).
 * The Cognito access token does not embed roleCode as a standard claim — the server
 * reads it from the `tenant_users` table after verifying the JWT sub.
 *
 * Client-side role determination: look for a `custom:roleCode` claim in the
 * decoded access token payload. If absent, return null (role unknown) and the
 * calling component defaults to hiding the elevated-tier UI (e.g. "Force Assign Back").
 * The server is the AUTHORITATIVE gate — the client gate is UX-only (T-09-04-06).
 *
 * This approach avoids introducing a new API endpoint for role-only queries while
 * still wiring the DISPATCH_MANAGER UX gate. Plan 09-05 or a future plan may add
 * a /v1/users/me/profile endpoint that returns roleCode explicitly.
 *
 * DEFAULT BEHAVIOR: returns null when the role cannot be determined.
 * The "Force Assign Back" menu item is hidden when roleCode is null (safe default).
 *
 * Spec: T-09-04-06 (client gate is UX-only; server is authoritative).
 */

import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Decodes the JWT payload from a base64url-encoded JWT string.
 * Returns null on any parse error — never throws.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || parts[1] === undefined) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Reads the caller's roleCode from the Cognito access token's `custom:roleCode` claim.
 *
 * Returns:
 * - A `string` role code (e.g. `'DISPATCH_MANAGER'`) if found in the token.
 * - `null` if the claim is absent, the token is not available, or an error occurs.
 *
 * The client role gate is UX-only; the server authoritatively enforces the DISPATCH_MANAGER
 * tier for the bulk-assign elevated path (T-09-04-06).
 */
export function useRoleCode(): string | null {
  const [roleCode, setRoleCode] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const resolve = async (): Promise<void> => {
      try {
        const session = await fetchAuthSession();
        const tok = session.tokens?.accessToken;
        if (tok === undefined) return;

        // Amplify's JWT token object has a `.toString()` method or payload accessor
        const tokenString: string = tok.toString();
        const payload = decodeJwtPayload(tokenString);
        if (payload === null) return;

        // Try standard claim names; Cognito custom attributes use 'custom:roleCode'
        const role =
          (payload['custom:roleCode'] as string | undefined) ??
          (payload['roleCode'] as string | undefined) ??
          null;

        if (mounted && typeof role === 'string') {
          setRoleCode(role);
        }
      } catch {
        // Session unavailable — leave roleCode as null (safe default: hide elevated items)
      }
    };

    void resolve();

    return () => {
      mounted = false;
    };
  }, []);

  return roleCode;
}
